import { Step, useComputed } from "@homebound/beam";
import { useFormState } from "@homebound/form-state";
import { ReactNode, useEffect, useMemo } from "react";
import { useStepperContext } from "src/components/stepper";
import { DefineChangeStep } from "src/routes/projects/change-events/guided/components/1.DefineChangeStep";
import { GuidedChangeEventStepActions } from "src/routes/projects/change-events/guided/components/GuidedChangeEventStepActions";
import { GuidedChangeEventStep } from "src/routes/projects/change-events/guided/utils";
import { BaseFormState, GuidedWorkflow } from "src/routes/projects/change-events/guided/workflows/types";

type WorkflowRunnerProps = { workflow: GuidedWorkflow<any>; base: BaseFormState; projectId: string };

/**
 * Drives a single workflow: owns its typed form, wires its `useActions`, and renders the
 * current step's content plus the shared step actions. Mounted with a `key={workflow}` by
 * the wizard so switching change type gives a fresh form and a stable `useActions` hook identity.
 */
export function WorkflowRunner(props: WorkflowRunnerProps) {
  const { workflow, base, projectId } = props;
  const { currentStep, setSteps } = useStepperContext();

  const form = useFormState({ config: workflow.formConfig });
  const actions = workflow.useActions({ base, form, projectId });

  // Step 1 ("Describe Change") is shared chrome and reads the base form; the rest come from
  // the workflow. Each step is gated behind the previous step's completion.
  const steps = useComputed<Step[]>(() => {
    const step1Complete = base.valid;
    const completes = [step1Complete, ...workflow.steps.map((s) => s.isComplete(form))];
    const step1: Step = {
      label: "Describe Change",
      value: GuidedChangeEventStep.DefineChange,
      state: step1Complete ? "complete" : "incomplete",
    };
    const rest = workflow.steps.map((s, i): Step => {
      const explicitlyDisabled = s.disabled?.(form) ?? false;
      const gatedByPrevious = !completes[i];
      return {
        label: s.label,
        value: s.value,
        state: s.isComplete(form) ? "complete" : "incomplete",
        disabled: explicitlyDisabled || gatedByPrevious,
      };
    });
    return [step1, ...rest];
  }, [base, form, workflow]);

  // Push the computed steps into the stepper. The page seeds static initial steps, so this
  // (and the stable ref from the page) won't fight the provider's initSteps sync effect.
  useEffect(() => setSteps(steps), [steps, setSteps]);

  const content = useMemo<ReactNode>(() => {
    if (currentStep.value === GuidedChangeEventStep.DefineChange) {
      return <DefineChangeStep formState={base} />;
    }
    const step = workflow.steps.find((s) => s.value === currentStep.value);
    return step?.render(form) ?? null;
  }, [currentStep.value, workflow, base, form]);

  const canAdvance = useComputed(() => {
    if (currentStep.value === GuidedChangeEventStep.DefineChange) {
      return base.valid;
    }
    const step = workflow.steps.find((s) => s.value === currentStep.value);
    return step ? step.isComplete(form) : false;
  }, [currentStep.value, base, form, workflow]);

  const canSubmit = useComputed(() => form.valid, [form]);

  return (
    <>
      {content}
      <GuidedChangeEventStepActions
        projectId={projectId}
        actions={actions}
        canAdvance={canAdvance}
        canSubmit={canSubmit}
      />
    </>
  );
}
